//ReviewComponent.tsx
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import theme from '../../../../style/theme';
import { getReviewManagementData } from '../../../../service/HomePage/ReviewManagementService';
import { ReviewManagementType } from './ReviewManagementType';
import ReviewCard from './ReviewCard';
import { useUser } from '../../../../context/UserContext';
import { useMediaQuery } from '@mui/material';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';

/**
 * タスク待ち申請一覧コンポーネント
 * - ログインユーザーの承認待ち申請をカード形式で一覧表示する
 * - 急ぎ申請を優先的に上部に表示し、申請日昇順でソートする
 * - 承認履歴・応答履歴ページへのリンクボタンも提供する
 */
export default function ReviewComponent() {
  const { user } = useUser();//ログイン中のユーザ情報を取得
  const [records, setRecords] = useState<ReviewManagementType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const isMobile = useMediaQuery('(max-width:600px)');  // 600px 以下でモバイル表示

  useEffect(() => {
    // 初期表示時にサーバーからタスク待ち申請データを取得する
    const fetchData = async () => {
      try {
        if (user && user.id){
          const data = await getReviewManagementData();
        setRecords(data);
      } else {
        console.error("ユーザー情報が取得できませんでした");
      }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  const sortedRecords = records.sort((a, b) => {
      if (a.isUrgent !== b.isUrgent) return b.isUrgent ? 1 : -1;
      return new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime();
    });

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Card
        variant="elevation"
        sx={{
          boxShadow: 2,
          borderRadius: 1,
          overflow: "hidden",
          background: theme.palette.secondary.main,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center'
          }}>
          <Typography variant="h2" sx={{ px: 2, py: 1 }}>タスク待ち申請</Typography>
          <Chip color="info" label={`${sortedRecords.length}件`}
            sx={{
              mr: 1, color: '#333333', maxHeight: '25px',
              bgcolor: theme.palette.custom.lightBlue, fontWeight: 'bold',
            }} variant='largeText' />
        </Box>
        <Divider />
        <ResizableBox
          width={Infinity}
          height={200}
          minConstraints={[Infinity, 200]}
          maxConstraints={[Infinity, 600]}
          resizeHandles={['se']} // 右下の角でリサイズ
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflowY: "auto",
              padding: 1,
              backgroundColor: "#f5f5f5",
              minHeight: '200px',
              resize: "vertical",
            }}
          >
            <List sx={{ padding: 0 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {sortedRecords.map((record) => (
                  <ReviewCard key={record.id} record={record} />
                ))}
              </Box>
            </List>
          </Box>
        </ResizableBox>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, m: 0.5 }}>
          <Box>
            <Link to="/ReviewManagement">
              <Button
                variant="contained"
                color="primary"
              >
                承認履歴
              </Button>
            </Link>
          </Box>
          <Box>
            <Link to="/AwaitingReport">
              <Button
                variant="contained"
                color="primary"
              >
                応答履歴
              </Button>
            </Link>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
